import { getDemoBrowserId } from "./demoFlow";

function safeText(value) {
  return value == null ? "" : String(value);
}

function createAiUsageError({ code, status, message, data }) {
  const normalizedCode = safeText(code).trim() || "ai_limit_error";
  const normalizedMessage =
    safeText(message).trim() || "AI generation is currently unavailable. Please try again later.";

  let title = "AI generation unavailable";
  let body = normalizedMessage;
  let retryable = false;

  if (normalizedCode === "ai_hourly_limit_reached") {
    title = "Hourly AI limit reached";
    body = normalizedMessage;
    retryable = false;
  } else if (normalizedCode === "ai_daily_limit_reached") {
    title = "Daily AI limit reached";
    body = normalizedMessage;
    retryable = false;
  } else if (normalizedCode === "ai_cooldown_active") {
    title = "Please wait before generating again";
    body = normalizedMessage;
    retryable = true;
  } else if (normalizedCode === "ai_regeneration_limit_reached") {
    title = "Regeneration limit reached";
    body = normalizedMessage;
    retryable = false;
  }

  const err = new Error(normalizedMessage);
  err.code = normalizedCode;
  err.status = Number(status || 0) || 429;
  err.title = title;
  err.body = body;
  err.details = [];
  err.retryable = retryable;
  err.data = data && typeof data === "object" ? data : null;
  return err;
}

function emitAiUsageError(err) {
  if (typeof window === "undefined") return;
  try {
    const detail = {
      code: err?.code || "ai_limit_error",
      status: Number(err?.status || 0) || 429,
      title: err?.title || "AI generation unavailable",
      body: err?.body || err?.message || "Please try again later.",
      retryable: Boolean(err?.retryable),
    };
    const event = new CustomEvent("ai-usage-error", { detail });
    window.dispatchEvent(event);
    if (typeof document !== "undefined") {
      document.dispatchEvent(new CustomEvent("ai-usage-error", { detail }));
    }
  } catch {
    // ignore
  }
}

export async function consumeAiUsage({ operation, projectId, isRegeneration = false } = {}) {
  const headers = { "Content-Type": "application/json" };
  const demoSessionId = getDemoBrowserId();
  if (demoSessionId) headers["x-browser-session-id"] = demoSessionId;

  const res = await fetch("/api/ai/consume", {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({
      operation: safeText(operation).trim() || "generation",
      projectId: safeText(projectId).trim() || null,
      isRegeneration: Boolean(isRegeneration),
    }),
  });
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }
  if (!res.ok) {
    const message = safeText(data?.error || data?.message || "AI limit reached. Try again later.").trim();
    const error = createAiUsageError({
      code: data?.code,
      status: res.status,
      message,
      data,
    });
    emitAiUsageError(error);
    throw error;
  }
  return data;
}
